<template>
  <div class="plan-summary">
    <div class="plan-card">
      <div class="plan-card__info">
        <p class="plan-card__title">{{ planData.title }}</p>
        <p class="plan-card__description caption">{{ planData.description }}</p>
      </div>

      <ul class="plan-card__features caption" v-if="planData.features?.length">
        <li
          class="plan-card__feature-item"
          v-for="(feature, index) in planData.features"
          :key="index"
        >
          {{ feature }}
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup>
import { computed } from "vue";
import { plans } from "@/constants/plans";
import { useStore } from "vuex";
import { useI18n } from 'vue-i18n';
import { localizePlan } from '@/i18n/planCatalog';

const store = useStore();
const { t, tm } = useI18n();
const apiPlans = computed(() => store.getters.getPlanDetails);

const props = defineProps({
  planName: {
    type: String,
    required: true,
  },
  status: {
    type: String,
    required: true,
  },
  subscriptionId: {
    type: String,
    default: null,
  },
  expiryDate: {
    type: String,
    default: null,
  },
  isAnnual: {
    type: Boolean,
    default: false,
  },
});

const planData = computed(() => {
  // Check if we have API plans
  if (apiPlans.value) {
    // Check if the plan exists in the API data
    for (const [key, plan] of Object.entries(apiPlans.value)) {
      if (
        props.planName === key ||
        (props.planName === "Basic" && key === "Basic") ||
        (props.planName === "Pro" && key === "Pro") ||
        (props.planName === "Freemium" && key === "Freemium") ||
        (props.planName === "Enterprise" && key === "Enterprise")
      ) {
        return localizePlan({
          title: plan.title,
          description: plan.description,
          features: plan.features,
        }, t, tm);
      }
    }
  }

  // Fallback to hardcoded plans if not found in API data
  return localizePlan(plans[props.planName] || plans.Freemium, t, tm);
});
</script>

<style scoped>
.plan-summary {
  width: 100%;
  max-width: 400px;
}

.plan-card__info {
  margin-bottom: var(--spacing-xl);
}

.plan-card__title {
  color: var(--color-white-70);
  font-size: var(--font-size-lg);
  margin-bottom: var(--spacing-sm);
}

.plan-card__description {
  color: var(--color-white-70);
  margin-bottom: var(--spacing-lg);
  font-size: var(--font-size-md);
}

.plan-card__features {
  list-style: disc;
  padding: 0 0 0 var(--spacing-xl);
  margin: var(--spacing-xl) 0;
}

.plan-card__feature-item {
  text-align: left;
  margin: var(--spacing-sm) 0;
  color: var(--color-white-70);
}

.status-container {
  margin-bottom: var(--spacing-xl);
}

.status {
  padding: var(--spacing-xs) var(--spacing-md);
  border-radius: var(--border-radius-xs);
  font-size: var(--font-size-md);
  font-weight: 500;
}

.status-active {
  background: var(--gradient-primary);
  color: var(--color-white);
}

.status-inactive {
  background: var(--color-error-10);
  color: var(--color-error);
}

.status-free {
  background: var(--color-white-10);
  color: var(--color-white-70);
}

.plan-meta {
  margin-top: var(--spacing-xl);
  padding-top: var(--spacing-xl);
  border-top: 1px solid var(--color-white-10);
  color: var(--color-white-40);
}

.plan-meta p {
  margin: var(--spacing-xs) 0;
}

.meta-row {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin: var(--spacing-sm) 0;
}

.meta-label {
  color: var(--color-white-40);
}

.meta-value {
  color: var(--color-white-70);
}
</style>
